import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import GlobalStyles from './styles/GlobalStyles';
import Header from './components/Header';
import Navigation from './components/Navigation';
import QuoteCarousel from './components/QuoteCarousel';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import FavoritesView from './components/FavoritesView';
import ActionPrompt from './components/ActionPrompt';
import VillainCreator from './components/VillainCreator';
import VillainDashboard from './components/VillainDashboard';
import AiMotivation from './components/AiMotivation';
import LandingPage from './components/LandingPage';
import { FavoritesProvider } from './context/FavoritesContext';
import { VillainProvider } from './context/VillainContext';
import { MotivationProvider } from './context/MotivationContext';
import { fetchQuotes } from './services/apiService';

// Small set of fallback quotes in case MongoDB connection fails
const fallbackQuotes = [
  {
    id: 1,
    text: "You have power over your mind – not outside events. Realize this, and you will find strength.",
    author: "Marcus Aurelius",
    philosopher: "marcus-aurelius"
  },
  {
    id: 2,
    text: "We suffer more often in imagination than in reality.",
    author: "Seneca",
    philosopher: "seneca"
  },
  {
    id: 3,
    text: "It's not what happens to you, but how you react to it that matters.",
    author: "Epictetus",
    philosopher: "epictetus"
  }
];

function App() {
  const [loading, setLoading] = useState(true);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showVillainCreator, setShowVillainCreator] = useState(false);
  const [activeSection, setActiveSection] = useState('quotes');
  const [quotes, setQuotes] = useState(fallbackQuotes);
  const [showLandingPage, setShowLandingPage] = useState(true); // Show landing page by default
  const loadingQuote = fallbackQuotes[Math.floor(Math.random() * fallbackQuotes.length)].text;

  // Add a state variable to track API status
  const [apiStatus, setApiStatus] = useState({ loading: true, error: null, success: false });

  useEffect(() => {
    // Fetch quotes from API and handle loading state
    const getQuotes = async () => {
      setApiStatus({ loading: true, error: null, success: false });

      try {
        console.log('Fetching quotes from API at:', new Date().toISOString());
        console.log('Using fallback quotes as backup if needed:', fallbackQuotes.length);

        const data = await fetchQuotes();
        console.log('API Response received:', data ? data.length : 0, 'quotes');
        setApiStatus({ loading: false, error: null, success: true });

        if (data && Array.isArray(data) && data.length > 0) {
          // Transform MongoDB _id to id for frontend compatibility
          const formattedQuotes = data.map((quote, index) => {
            // Make sure we have all required fields
            if (!quote.text || !quote.author) {
              console.warn('Quote missing required fields:', quote);
              return null;
            }

            return {
              id: quote._id || index + 1,
              text: quote.text,
              author: quote.author,
              philosopher: quote.author.toLowerCase().replace(/\s+/g, '-') // Generate philosopher slug, handle multiple spaces
            };
          }).filter(quote => quote !== null); // Remove any null entries

          console.log('Formatted Quotes:', formattedQuotes);

          if (formattedQuotes.length > 0) {
            setQuotes(formattedQuotes);
          } else {
            console.warn('No valid quotes after formatting, using fallback quotes');
          }
        } else {
          console.warn('No quotes returned from API or invalid data format, using fallback quotes');
        }
      } catch (error) {
        console.error('Failed to fetch quotes from API, using fallback quotes:', error);
        // Keep using the default quotes if API fails
        setApiStatus({ loading: false, error: error.message || 'Failed to fetch quotes', success: false });
      } finally {
        // Simulate minimum loading time for UX
        setTimeout(() => {
          setLoading(false);
          console.log('App loading complete, API status:', apiStatus);
        }, 2000);
      }
    };

    getQuotes();

    return () => {
      // Cleanup if needed
    };
  }, []);

  const toggleFavorites = () => {
    setShowFavorites(prev => !prev);
    if (showVillainCreator) setShowVillainCreator(false);
  };

  const toggleVillainCreator = () => {
    setShowVillainCreator(prev => !prev);
    if (showFavorites) setShowFavorites(false);
  };

  // Handle entering the app from landing page
  const handleEnterApp = (mode) => {
    // For now, just hide the landing page
    // In the future, this could handle actual login/signup functionality
    setShowLandingPage(false);
    console.log(`User entered app as: ${mode}`);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'quotes':
        return (
          <>
            <QuoteCarousel quotes={quotes} />
            <FavoritesButton onClick={toggleFavorites}>
              {showFavorites ? "Hide Favorites" : "View Favorites ★"}
            </FavoritesButton>
            {showFavorites && <FavoritesView onClose={toggleFavorites} />}
          </>
        );
      case 'villain':
        return (
          <>
            <VillainDashboard onEdit={toggleVillainCreator} />
            <VillainButton onClick={toggleVillainCreator}>
              {showVillainCreator ? "Hide Editor" : "Create/Edit Villain"}
            </VillainButton>
            {showVillainCreator && <VillainCreator onClose={toggleVillainCreator} />}
          </>
        );
      case 'motivation':
        return <AiMotivation />;
      default:
        return <QuoteCarousel quotes={quotes} />;
    }
  };

  return (
    <FavoritesProvider>
      <VillainProvider>
        <MotivationProvider>
          <AppContainer>
            <GlobalStyles />
            {loading ? (
              <LoadingScreen quote={loadingQuote} />
            ) : showLandingPage ? (
              // Show landing page if showLandingPage is true
              <LandingPage onEnterApp={handleEnterApp} />
            ) : (
              // Show main app if showLandingPage is false
              <>
                <Header />
                <Navigation activeSection={activeSection} setActiveSection={setActiveSection} />
                <Main>
                  {renderActiveSection()}

                  {/* Debug info - remove in production */}
                  {process.env.NODE_ENV !== 'production' && (
                    <DebugInfo>
                      <h4>API Status</h4>
                      <p>Loading: {apiStatus.loading ? 'Yes' : 'No'}</p>
                      <p>Success: {apiStatus.success ? 'Yes' : 'No'}</p>
                      <p>Error: {apiStatus.error || 'None'}</p>
                      <p>Quotes: {quotes.length}</p>
                      <p>Landing Page: {showLandingPage ? 'Visible' : 'Hidden'}</p>
                      <button
                        onClick={async () => {
                          try {
                            setApiStatus({ loading: true, error: null, success: false });
                            const data = await fetchQuotes();
                            if (data && Array.isArray(data) && data.length > 0) {
                              const formattedQuotes = data.map((quote, index) => ({
                                id: quote._id || index + 1,
                                text: quote.text,
                                author: quote.author,
                                philosopher: quote.author.toLowerCase().replace(/\s+/g, '-')
                              }));
                              setQuotes(formattedQuotes);
                              setApiStatus({ loading: false, error: null, success: true });
                            }
                          } catch (error) {
                            setApiStatus({ loading: false, error: error.message, success: false });
                          }
                        }}
                        style={{
                          backgroundColor: '#333',
                          color: '#fff',
                          border: '1px solid #666',
                          padding: '5px 10px',
                          cursor: 'pointer',
                          marginTop: '10px'
                        }}
                      >
                        Refresh Quotes
                      </button>
                      <button
                        onClick={() => setShowLandingPage(true)}
                        style={{
                          backgroundColor: '#333',
                          color: '#fff',
                          border: '1px solid #666',
                          padding: '5px 10px',
                          cursor: 'pointer',
                          marginTop: '10px',
                          marginLeft: '5px'
                        }}
                      >
                        Show Landing Page
                      </button>
                    </DebugInfo>
                  )}
                </Main>
                <Footer />
                <ActionPrompt />
              </>
            )}
          </AppContainer>
        </MotivationProvider>
      </VillainProvider>
    </FavoritesProvider>
  );
}

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--color-background);
`;

const Main = styled.main`
  flex: 1;
  padding: 0 1rem;
  margin-top: -2rem; /* Move the quote card higher up */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start; /* Align to the top instead of center */
  position: relative;
`;

const Button = styled.button`
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border: 1px solid;
  border-radius: 30px;
  padding: 0.6rem 1.2rem;
  font-family: var(--font-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }

  &:focus {
    outline: none;
  }
`;

const FavoritesButton = styled(Button)`
  color: #d4af37;
  border-color: #d4af37;
  margin-top: 1.5rem;

  &:hover {
    background: #d4af37;
    color: #000;
    box-shadow: 0 5px 15px rgba(212, 175, 55, 0.3);
  }
`;

const VillainButton = styled(Button)`
  color: #a33;
  border-color: #a33;
  margin-top: 1.5rem;

  &:hover {
    background: #a33;
    color: #fff;
    box-shadow: 0 5px 15px rgba(170, 51, 51, 0.3);
  }
`;

const MotivationButton = styled(Button)`
  color: #4682b4;
  border-color: #4682b4;
  margin-top: 1.5rem;

  &:hover {
    background: #4682b4;
    color: #fff;
    box-shadow: 0 5px 15px rgba(70, 130, 180, 0.3);
  }
`;

const DebugInfo = styled.div`
  position: fixed;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.8);
  color: #00ff00;
  padding: 10px;
  border-radius: 5px;
  font-family: monospace;
  font-size: 12px;
  z-index: 9999;
  max-width: 300px;

  h4 {
    margin-top: 0;
    margin-bottom: 5px;
    color: #ff9900;
  }

  p {
    margin: 2px 0;
  }
`;

export default App;
